/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable, effect, signal } from '@angular/core';
import { Theme } from '../../shared/models/theme.model';
import { LocalStorageService } from './local-storage.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  userId!: any;
  public theme = signal<Theme>({ mode: 'system', color: 'violet' });
  public emitTheme: BehaviorSubject<Theme> = new BehaviorSubject(this.theme());
  public themeColors = [
    {
      name: 'violet',
      code: '#6a21a6',
    },
    {
      name: 'blue',
      code: '#3b82f6',
    },
    {
      name: 'green',
      code: '#0E9F6E',
    },
    {
      name: 'yellow',
      code: '#f59e0b',
    },
    {
      name: 'orange',
      code: '#ea580c',
    },
    {
      name: 'red',
      code: '#cc0022',
    },
    {
      name: 'neutral',
      code: '#18181b',
    },
  ];
  public themeMode = ['system', 'light', 'dark'];
  private apiUrl = environment.apiUrl;
  base_Url = 'api/setting';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private storage: LocalStorageService,
  ) {
    this.userId = this.authService.currentUser._id;
    this.loadTheme();
    effect(() => {
      this.setTheme();
    });
    this.authService.userLoggedIn.subscribe(loggedIn => {
      if (loggedIn && this.userId) {
        this.getAppearence(this.userId).subscribe((theme: Theme) => {
          this.theme.set({ mode: theme.mode, color: theme.color });
        });
      }
    });
    this.authService.userLoggedOut.subscribe(loggedOut => {
      if (loggedOut) {
        this.theme.set({ mode: 'light', color: 'violet' });
      }
    });
  }

  private loadTheme() {
    const theme = this.storage.getItem('theme');
    if (theme) {
      this.theme.set(JSON.parse(theme));
    }
    // else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    //   this.theme.set({ mode: 'dark', color: 'violet' });
    // }
    else {
      this.theme.set({ mode: 'light', color: 'violet' });
    }
  }

  private setTheme() {
    this.storage.setItem('theme', JSON.stringify(this.theme()));
    this.setThemeClass();
  }

  public get isDark(): boolean {
    return (
      this.theme().mode == 'dark' ||
      (!this.theme().mode &&
        window.matchMedia('(prefers-color-scheme: dark)').matches)
    );
  }

  private setThemeClass() {
    if (
      window.matchMedia('(prefers-color-scheme: dark)').matches &&
      this.theme().mode == 'system'
    ) {
      document.querySelector('html')!.className = 'dark';
    } else if (this.theme().mode == 'system') {
      document.querySelector('html')!.className = 'light';
    } else {
      document.querySelector('html')!.className = this.theme().mode;
    }
    document
      .querySelector('html')!
      .setAttribute('data-theme', this.theme().color);
    this.emitTheme.next(this.theme());
  }

  setAppearence() {
    this.updateAppearence(this.userId, this.theme()).subscribe(() => {});
  }

  updateAppearence(uid: any, data: Theme) {
    const API = `${this.apiUrl}/${this.base_Url}/pref/appearence/${uid}`;
    return this.http.put(API, data);
  }

  getAppearence(uid: any): Observable<Theme> {
    const API = `${this.apiUrl}/${this.base_Url}/pref/appearence/${uid}`;
    return this.http.get<Theme>(API);
  }

  changeColor(color: string, amount: number) {
    const clamp = (val: number) => Math.min(Math.max(val, 0), 0xff);
    const fill = (str: string) => ('00' + str).slice(-2);

    const num = parseInt(color.substr(1), 16);
    const red = clamp((num >> 16) + amount);
    const green = clamp(((num >> 8) & 0x00ff) + amount);
    const blue = clamp((num & 0x0000ff) + amount);
    return (
      '#' +
      fill(red.toString(16)) +
      fill(green.toString(16)) +
      fill(blue.toString(16))
    );
  }

  public get themeValue(): Theme {
    return this.theme();
  }
}
